import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authAPI, UserLogin, getUserErrorMessage } from '../api/api';
import { BRAND_ASSETS } from '../brand/assets';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<UserLogin>({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authAPI.login(formData);
      localStorage.setItem('token', response.access_token);
      const me = await authAPI.getMe();
      navigate(me.email_verified ? '/home' : '/verify-email');
    } catch (err: any) {
      console.error('Login error:', err);
      setError(getUserErrorMessage(
        err,
        'Login failed. Check your email and password, or register if you do not have an account yet.'
      ));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="container public-auth-page"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 16px 60px' }}
    >
      <div className="card" style={{ maxWidth: '400px', width: '100%' }}>
        <motion.img
          src={BRAND_ASSETS.logo}
          alt="WisemenResearch"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{
            display: 'block',
            width: '180px',
            height: 'auto',
            margin: '0 auto 24px',
          }}
        />
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{ 
            textAlign: 'center', 
            marginBottom: '30px',
            color: 'var(--neon-cyan)',
            fontSize: '2.5rem'
          }}
        >
          Welcome Back
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          style={{ 
            textAlign: 'center', 
            marginBottom: '30px',
            color: 'var(--text-secondary)',
            fontSize: '1.1rem'
          }}
        >
          Sign in to your WisemenResearch account
        </motion.p>

        {error && <div className="error">{error}</div>}

        <form onSubmit={handleSubmit} aria-label="Sign in">
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="form-input"
              required
              placeholder="Enter your email"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="form-input"
              required
              placeholder="Enter your password"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
            style={{ width: '100%', marginTop: '20px' }}
          >
            {loading ? <div className="spinner" style={{ margin: '0 auto' }} /> : 'Login'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <p style={{ color: 'var(--text-secondary)' }}>
            Don't have an account?{' '}
            <Link to="/register" style={{ color: 'var(--neon-cyan)', textDecoration: 'none' }}>
              Register here
            </Link>
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default Login;
